if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.modalContent) this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      buildFetchUrl(productUrl) {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.delete('section_id');
        return url.toString();
      }

      findProductSection(doc) {
        return (
          doc.querySelector('section[id^="FumiMainProduct-"]') ||
          doc.querySelector('section[id^="MainProduct-"]') ||
          doc.querySelector('[data-fumi-main-product]') ||
          doc.querySelector('product-info')?.closest('section')
        );
      }

      loadFumiAssets(doc) {
        doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          const href = link.getAttribute('href');
          if (!href?.includes('section-fumi-main-product')) return;
          if (document.querySelector(`link[href="${href}"]`)) return;
          document.head.appendChild(link.cloneNode(true));
        });

        const loaders = ['product-info.js', 'fumi-main-product.js'].map((assetName) => {
          if (assetName === 'fumi-main-product.js' && typeof window.FumiMainProduct === 'function') {
            return Promise.resolve();
          }

          const tag = doc.querySelector(`script[src*="${assetName}"]`);
          if (!tag) return Promise.resolve();

          const src = tag.getAttribute('src');
          if (document.querySelector(`script[src="${src}"]`)) {
            return new Promise((resolve) => {
              if (assetName !== 'fumi-main-product.js' || typeof window.FumiMainProduct === 'function') {
                resolve();
                return;
              }
              const check = (attempts = 0) => {
                if (typeof window.FumiMainProduct === 'function' || attempts > 40) {
                  resolve();
                  return;
                }
                setTimeout(() => check(attempts + 1), 50);
              };
              check();
            });
          }

          return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => resolve();
            document.head.appendChild(script);
          });
        });

        return Promise.all(loaders);
      }

      extractQuickAddHTML() {
        const fumiBuy = this.productElement.querySelector('.fumi-buy');
        if (fumiBuy) {
          const wrapper = document.createElement('div');
          wrapper.className = 'fumi-main-product fumi-main-product--quick-add';
          wrapper.setAttribute('data-fumi-main-product', '');
          if (this.productElement.dataset.section) {
            wrapper.dataset.section = this.productElement.dataset.section;
          }
          if (this.productElement.dataset.stoqVariantIds) {
            wrapper.dataset.stoqVariantIds = this.productElement.dataset.stoqVariantIds;
          }
          wrapper.innerHTML = fumiBuy.innerHTML;
          return wrapper.outerHTML;
        }

        return this.productElement.innerHTML;
      }

      initFumiQuickAdd() {
        const fumiRoot = this.modalContent.querySelector('[data-fumi-main-product]');
        if (!fumiRoot || typeof window.FumiMainProduct !== 'function') return;
        new window.FumiMainProduct(fumiRoot);
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const fetchUrl = this.buildFetchUrl(opener.getAttribute('data-product-url'));

        fetch(fetchUrl)
          .then((response) => {
            if (!response.ok) throw new Error(`Quick add failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = this.findProductSection(responseHTML);

            if (!this.productElement || !this.modalContent) {
              console.error('Quick add: product section not found');
              return;
            }

            return this.loadFumiAssets(responseHTML).then(() => {
              this.productElement.classList.forEach((classApplied) => {
                if (classApplied.startsWith('color-') || classApplied === 'gradient') {
                  this.modalContent.classList.add(classApplied);
                }
              });

              this.preventDuplicatedIDs();
              this.removeDOMElements();

              const html = this.extractQuickAddHTML();
              this.setInnerHTML(this.modalContent, html);

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }

              if (window.ProductModel) window.ProductModel.loadShopifyXR();

              this.removeGalleryListSemantic();
              this.updateImageSizes();
              this.preventVariantURLSwitching();
              this.initFumiQuickAdd();
              super.show(opener);
            });
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            const doneSpinner = opener.querySelector('.loading__spinner');
            if (doneSpinner) doneSpinner.classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        if (!this.productElement) return;

        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        this.productElement.querySelector('.fumi-gallery')?.remove();
        this.productElement.querySelector('[data-fumi-zoom-modal]')?.remove();
        this.productElement.querySelector('[data-fumi-size-modal]')?.remove();
        this.productElement.querySelector('.fumi-zoom-modal')?.remove();
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        if (!sectionId || sectionId.length < 12) return;

        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        if (!product) return;

        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
